import { blankTier } from '../lib/defaults';
import { tierColor } from '../lib/palette';
import { AGGREGATES, type Aggregate, type TierConfig } from '../lib/types';
import { TextField } from './Field';

interface Props {
  tiers: TierConfig[];
  onChange: (next: TierConfig[]) => void;
  invalidPaths: Set<string>;
  /** Human resolution of the input to each tier, for the "reads from" line. */
  sourceLabels: string[];
}

export default function TiersPanel({ tiers, onChange, invalidPaths, sourceLabels }: Props) {
  const patch = (index: number, delta: Partial<TierConfig>) =>
    onChange(tiers.map((t, i) => (i === index ? { ...t, ...delta } : t)));

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= tiers.length) return;
    const next = [...tiers];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const toggleAggregate = (index: number, agg: Aggregate, checked: boolean) => {
    const current = tiers[index].aggregates;
    const next = checked
      ? AGGREGATES.filter((a) => current.includes(a) || a === agg)
      : current.filter((a) => a !== agg);
    // A tier with no aggregate cannot be generated, so the last one is sticky.
    patch(index, { aggregates: next.length > 0 ? [...next] : current });
  };

  return (
    <section className="panel" aria-labelledby="tiers-heading">
      <header>
        <h2 id="tiers-heading">Tiers</h2>
        <span className="panel-note">{tiers.length} in cascade</span>
      </header>
      <div className="panel-body">
        {tiers.map((tier, i) => (
          <div className="tier" key={tier.id} style={{ ['--tier-color' as string]: tierColor(i) }}>
            <div className="tier-head">
              <h3>
                Tier {i + 1} — reads {sourceLabels[i] ?? 'raw'}
              </h3>
              <div className="tier-actions">
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Move tier ${i + 1} earlier`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Move tier ${i + 1} later`}
                  disabled={i === tiers.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Remove tier ${i + 1}`}
                  onClick={() => onChange(tiers.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            </div>

            <TextField
              label="Target bucket"
              value={tier.bucket}
              onChange={(bucket) => patch(i, { bucket })}
              testId={`tier-${i}-bucket`}
            />
            <div className="grid-2">
              <TextField
                label="Window"
                hint="e.g. 1m, 1h, 1d"
                value={tier.window}
                invalid={invalidPaths.has(`tiers.${i}.window`)}
                onChange={(window) => patch(i, { window })}
                testId={`tier-${i}-window`}
              />
              <TextField
                label="Retention"
                hint="must exceed its input"
                value={tier.retention}
                invalid={invalidPaths.has(`tiers.${i}.retention`)}
                onChange={(retention) => patch(i, { retention })}
                testId={`tier-${i}-retention`}
              />
            </div>

            <fieldset className="agg-list">
              <legend>Aggregates</legend>
              {AGGREGATES.map((agg) => (
                <label className="agg" key={agg}>
                  <input
                    type="checkbox"
                    checked={tier.aggregates.includes(agg)}
                    data-testid={`tier-${i}-agg-${agg}`}
                    onChange={(e) => toggleAggregate(i, agg, e.target.checked)}
                  />
                  {agg}
                </label>
              ))}
            </fieldset>

            <label className="checkbox" style={{ marginTop: 10 }}>
              <input
                type="checkbox"
                checked={tier.createEmpty}
                onChange={(e) => patch(i, { createEmpty: e.target.checked })}
              />
              createEmpty — write null points for windows with no data
            </label>
          </div>
        ))}

        <button
          type="button"
          className="primary"
          data-testid="add-tier"
          onClick={() =>
            onChange([
              ...tiers,
              blankTier({ bucket: `rollup_${tiers.length + 1}`, window: '1d', retention: '730d' }),
            ])
          }
        >
          Add tier
        </button>
      </div>
    </section>
  );
}
